import type { Repository } from "../data/repository";
import {
  type CompleteOptions,
  type LLMProvider,
  LLMUnavailable,
} from "./provider";
import { isConfigured, loadSettings, type LlmSettings } from "./settings";

const ANTHROPIC_URL = "https://api.anthropic.com/v1/messages";
const ANTHROPIC_VERSION = "2023-06-01";

interface AnthropicResponse {
  content?: Array<{ type: string; text?: string }>;
}

interface AnthropicError {
  error?: { type?: string; message?: string };
}

// Anthropic Claude adapter. Two transports behind one interface: the stateless
// STRAD proxy (default), or the user's own key sent direct to the model (BYO).
// Settings are read per call so a change in the panel takes effect at once.
export class CloudProvider implements LLMProvider {
  constructor(private readonly repo: Repository) {}

  async isConfigured(): Promise<boolean> {
    return isConfigured(await loadSettings(this.repo));
  }

  async complete(opts: CompleteOptions): Promise<string> {
    const settings = await loadSettings(this.repo);
    if (!isConfigured(settings)) {
      // Be specific about WHAT is missing for the selected mode, so the fix is
      // obvious: a proxy URL for proxy mode, or a key (and the BYO radio) for
      // bring-your-own.
      throw new LLMUnavailable(
        settings.mode === "proxy"
          ? "No STRAD proxy is configured. In Settings, either choose \"Bring your own key\" and paste your Anthropic key, or set the proxy URL."
          : "No API key set. In Settings, paste your Anthropic key under \"Bring your own key\".",
      );
    }

    const body = JSON.stringify({
      model: settings.model,
      max_tokens: opts.maxTokens ?? 1024,
      temperature: opts.temperature ?? 0.4,
      system: opts.system,
      messages: opts.messages,
    });

    let res: Response;
    try {
      res = await fetch(endpoint(settings), {
        method: "POST",
        headers: headers(settings),
        body,
      });
    } catch (e) {
      throw new LLMUnavailable(`Could not reach the model (${String(e)}).`);
    }

    if (!res.ok) {
      // Surface the model's own error type/message (a bad key, an unavailable
      // model, a validation error) so a misconfiguration is diagnosable. These
      // error bodies carry the failure reason, not the user's rationale text.
      let detail = "";
      try {
        const body = (await res.json()) as AnthropicError;
        if (body.error?.message) {
          detail = ` ${body.error.type ?? "error"}: ${body.error.message}`;
        }
      } catch {
        // non-JSON body (e.g. a proxy/CORS failure); the status is the signal
      }
      const hint =
        res.status === 401
          ? " Check your API key in Settings."
          : res.status === 404
            ? " Check the model name in Settings."
            : "";
      throw new LLMUnavailable(`Model returned ${res.status}.${detail}${hint}`);
    }

    const json = (await res.json()) as AnthropicResponse;
    const text = (json.content ?? [])
      .filter((b) => b.type === "text" && b.text)
      .map((b) => b.text)
      .join("")
      .trim();
    if (!text) throw new LLMUnavailable("Model returned an empty response.");
    return text;
  }
}

function endpoint(s: LlmSettings): string {
  return s.mode === "proxy" ? s.proxyUrl.trim() : ANTHROPIC_URL;
}

function headers(s: LlmSettings): Record<string, string> {
  const base: Record<string, string> = { "content-type": "application/json" };
  if (s.mode === "byok") {
    base["x-api-key"] = s.apiKey.trim();
    base["anthropic-version"] = ANTHROPIC_VERSION;
    // Required for direct browser calls to the Anthropic API.
    base["anthropic-dangerous-direct-browser-access"] = "true";
  }
  return base;
}
